/* ================================================================
   SelectionToolbar — Floating toolbar that appears on text selection
   within assistant messages. Lets users highlight text and add notes.
   ================================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { HighlightColor } from '../highlights';
import { addHighlight, HIGHLIGHT_COLORS } from '../highlights';
import { useToast } from '../contexts/ToastContext';

interface Props {
  /** Called after a highlight is saved — parent should refresh highlight state */
  onHighlightAdded: () => void;
}

interface ToolbarState {
  visible: boolean;
  x: number;
  y: number;
  text: string;
  messageId: string;
  queryId: string | undefined;
  startOffset: number;
  endOffset: number;
  sessionContext: string | undefined;
}

const INITIAL: ToolbarState = {
  visible: false,
  x: 0,
  y: 0,
  text: '',
  messageId: '',
  queryId: undefined,
  startOffset: 0,
  endOffset: 0,
  sessionContext: undefined,
};

/**
 * Walk up from an element to find the closest `.message--assistant` container
 * and extract its data attributes.
 */
function findMessageContext(el: Node | null): {
  messageId: string;
  queryId: string | undefined;
  sessionContext: string | undefined;
} | null {
  let node: HTMLElement | null = el instanceof HTMLElement ? el : el?.parentElement ?? null;
  while (node) {
    if (node.classList?.contains('message--assistant')) {
      return {
        messageId: node.dataset.msgId || '',
        queryId: node.dataset.queryId || undefined,
        sessionContext: node.dataset.sessionContext || undefined,
      };
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Compute the character offset of a Range endpoint within the message-prose
 * container, by walking a TreeWalker over its text nodes.
 */
function computeOffset(
  proseEl: Element,
  container: Node,
  offset: number,
): number {
  const walker = document.createTreeWalker(proseEl, NodeFilter.SHOW_TEXT);
  let charCount = 0;
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    if (node === container) {
      return charCount + offset;
    }
    charCount += node.length;
  }
  // Fallback: container is an element node, offset = child index
  return charCount;
}

export default function SelectionToolbar({ onHighlightAdded }: Props) {
  const [state, setState] = useState<ToolbarState>(INITIAL);
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedColor, setSelectedColor] = useState<HighlightColor>('yellow');
  const toolbarRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const { addToast } = useToast();

  /* ── Listen for mouseup / touchend to detect selection ── */
  useEffect(() => {
    const handleSelectionChange = () => {
      // Small debounce — let the browser settle the selection
      requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !sel.rangeCount) {
          // Don't hide if note form is open
          if (!showNote) {
            setState(INITIAL);
          }
          return;
        }

        const range = sel.getRangeAt(0);
        const text = sel.toString().trim();
        if (!text || text.length < 2) return;

        // Must be inside an assistant message
        const ctx = findMessageContext(range.commonAncestorContainer);
        if (!ctx) return;

        // Find the .message-prose container for offset calculation
        const msgEl = range.commonAncestorContainer instanceof HTMLElement
          ? range.commonAncestorContainer.closest('.message--assistant')
          : range.commonAncestorContainer.parentElement?.closest('.message--assistant');
        const proseEl = msgEl?.querySelector('.message-prose');

        let startOffset = 0;
        let endOffset = 0;
        if (proseEl) {
          startOffset = computeOffset(proseEl, range.startContainer, range.startOffset);
          endOffset = computeOffset(proseEl, range.endContainer, range.endOffset);
        }

        // Position the toolbar above the selection
        const rect = range.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top - 8;

        setState({
          visible: true,
          x,
          y,
          text,
          messageId: ctx.messageId,
          queryId: ctx.queryId,
          startOffset,
          endOffset,
          sessionContext: ctx.sessionContext,
        });

        // One-time hint: show a toast the first time a user selects text
        try {
          if (!localStorage.getItem('ch-selection-hint-shown')) {
            localStorage.setItem('ch-selection-hint-shown', '1');
            // Brief delay so toolbar is visible first
            setTimeout(() => {
              const el = document.createElement('div');
              el.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#1e293b;border:1px solid #334155;color:#94a3b8;padding:8px 16px;border-radius:8px;font-size:12px;z-index:9999;pointer-events:none;transition:opacity 0.3s;white-space:nowrap;';
              el.textContent = '💡 Tip: select text to bookmark, highlight, or save to your research notes';
              document.body.appendChild(el);
              setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 300); }, 4000);
            }, 300);
          }
        } catch { /* ignore storage errors */ }
      });
    };

    document.addEventListener('mouseup', handleSelectionChange);
    document.addEventListener('touchend', handleSelectionChange);

    return () => {
      document.removeEventListener('mouseup', handleSelectionChange);
      document.removeEventListener('touchend', handleSelectionChange);
    };
  }, [showNote]);

  /* ── Click outside to dismiss ── */
  useEffect(() => {
    if (!state.visible) return;
    const handleClick = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setState(INITIAL);
        setShowNote(false);
        setNoteText('');
      }
    };
    // Delay slightly so the mouseup that opened us doesn't immediately close
    const id = setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [state.visible]);

  /* ── Focus note textarea when it appears ── */
  useEffect(() => {
    if (showNote && noteRef.current) {
      noteRef.current.focus();
    }
  }, [showNote]);

  /* ── Save highlight ── */
  const doHighlight = useCallback(
    (withNote: boolean) => {
      if (!state.text) return;
      addHighlight({
        id: crypto.randomUUID(),
        queryId: state.queryId,
        messageId: state.messageId,
        text: state.text,
        startOffset: state.startOffset,
        endOffset: state.endOffset,
        note: withNote ? noteText.trim() : '',
        color: selectedColor,
        created_at: new Date().toISOString(),
        sessionContext: state.sessionContext,
      });
      onHighlightAdded();
      addToast('success', 'Highlighted', 1500);
      setState(INITIAL);
      setShowNote(false);
      setNoteText('');
      // Clear browser selection
      window.getSelection()?.removeAllRanges();
    },
    [state, noteText, selectedColor, onHighlightAdded, addToast],
  );

  if (!state.visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="selection-toolbar"
      style={{
        left: `${state.x}px`,
        top: `${state.y}px`,
      }}
      role="toolbar"
      aria-label="Text annotation"
    >
      {!showNote ? (
        <>
          {/* Colour picker row */}
          <div className="selection-toolbar-colors">
            {(Object.keys(HIGHLIGHT_COLORS) as HighlightColor[]).map((c) => (
              <button
                key={c}
                className={`selection-color-dot${selectedColor === c ? ' selection-color-dot--active' : ''}`}
                style={{ background: HIGHLIGHT_COLORS[c].border }}
                onClick={(e) => { e.stopPropagation(); setSelectedColor(c); }}
                aria-label={HIGHLIGHT_COLORS[c].label}
                title={HIGHLIGHT_COLORS[c].label}
              />
            ))}
          </div>
          {/* Action buttons */}
          <div className="selection-toolbar-actions">
            <button
              className="selection-toolbar-btn"
              onClick={(e) => {
                e.stopPropagation();
                doHighlight(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="m16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838.838-2.872a2 2 0 0 1 .506-.854z" />
              </svg>
              Highlight
            </button>
            <button
              className="selection-toolbar-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowNote(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Add Note
            </button>
          </div>
        </>
      ) : (
        <div className="selection-toolbar-note">
          <textarea
            ref={noteRef}
            className="selection-toolbar-textarea"
            placeholder="Add a note…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                doHighlight(true);
              }
              if (e.key === 'Escape') {
                setShowNote(false);
                setNoteText('');
              }
            }}
            rows={3}
          />
          <div className="selection-toolbar-note-actions">
            <button
              className="selection-toolbar-btn selection-toolbar-btn--secondary"
              onClick={() => { setShowNote(false); setNoteText(''); }}
            >
              Cancel
            </button>
            <button
              className="selection-toolbar-btn selection-toolbar-btn--primary"
              onClick={() => doHighlight(true)}
            >
              Save
            </button>
          </div>
          <span className="selection-toolbar-hint">⌘+Enter to save</span>
        </div>
      )}
    </div>
  );
}
